import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import Sidebar from "../../layout/Sidebar/SideBar";
import Navbar from "../../layout/Navbar/Navbar";
import blogFetch from "../../axios/config";
import toast from "react-hot-toast";

interface ZferLiberado {
    id: number;
    codMaterial: string;
    criado_Por: string;
    _ts_Creat: string;
}

interface MaterialGenesis {
    codMaterial: string;
    partnumber: string;
    desProduto: string;
    formulaCod: string;
    vehBrand: string;
    vehModel: string;
    vehCode: string;
    subProduto: string;
    productType: string;
    jaCadastradoSemQrCode: boolean;
}

export function ZferSemQrcode(): JSX.Element {
    const [zfers, setZfers] = useState<ZferLiberado[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Modal adicionar
    const [showModal, setShowModal] = useState(false);
    const [inputZfer, setInputZfer] = useState("");
    const [materialEncontrado, setMaterialEncontrado] = useState<MaterialGenesis | null>(null);
    const [pesquisando, setPesquisando] = useState(false);
    const [adicionando, setAdicionando] = useState(false);

    // Modal excluir
    const [showModalExcluir, setShowModalExcluir] = useState(false);
    const [itemExcluir, setItemExcluir] = useState<ZferLiberado | null>(null);
    const [excluindo, setExcluindo] = useState(false);

    const fetchZfers = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await blogFetch.get<ZferLiberado[]>("engenharia/sem-qrcode");
            setZfers(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Erro ao buscar ZFERs:", error);
            toast.error("Erro ao carregar ZFERs liberados.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchZfers();
    }, [fetchZfers]);

    const abrirModal = () => {
        setInputZfer("");
        setMaterialEncontrado(null);
        setShowModal(true);
    };

    const fecharModal = () => {
        setShowModal(false);
        setInputZfer("");
        setMaterialEncontrado(null);
    };

    const handlePesquisar = async () => {
        const valor = inputZfer.trim();
        if (!valor) {
            toast.error("Informe o código do material.");
            return;
        }

        setPesquisando(true);
        setMaterialEncontrado(null);
        try {
            const { data } = await blogFetch.get<MaterialGenesis>(
                `engenharia/pesquisar-zfer/${encodeURIComponent(valor)}`
            );
            setMaterialEncontrado(data);
        } catch (error: any) {
            toast.error(error.response?.data || "Material não encontrado no Genesis.");
        } finally {
            setPesquisando(false);
        }
    };

    const handleAdicionar = async () => {
        if (!materialEncontrado) return;

        if (materialEncontrado.jaCadastradoSemQrCode) {
            toast.error("Esse material já está na lista.");
            return;
        }

        const user = JSON.parse(localStorage.getItem("user") || "{}");

        setAdicionando(true);
        try {
            await blogFetch.post("engenharia/sem-qrcode", {
                codMaterial: materialEncontrado.codMaterial,
                criado_Por: user.user_Matricula ?? "",
            });
            toast.success(`${materialEncontrado.codMaterial} adicionado!`);
            fecharModal();
            await fetchZfers();
        } catch (error: any) {
            toast.error(error.response?.data || "Erro ao adicionar material.");
        } finally {
            setAdicionando(false);
        }
    };

    const confirmarExclusao = (item: ZferLiberado) => {
        setItemExcluir(item);
        setShowModalExcluir(true);
    };

    const handleExcluir = async () => {
        if (!itemExcluir) return;

        setExcluindo(true);
        try {
            await blogFetch.delete(
                `engenharia/sem-qrcode/${encodeURIComponent(itemExcluir.codMaterial)}`
            );
            toast.success(`${itemExcluir.codMaterial} removido.`);
            setShowModalExcluir(false);
            setItemExcluir(null);
            await fetchZfers();
        } catch (error: any) {
            toast.error(error.response?.data || "Erro ao remover material.");
        } finally {
            setExcluindo(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handlePesquisar();
    };

    return (
        <div className="d-flex app_">
            <Sidebar />
            <Navbar />
            <div className="flex-grow-1">
                <div className="container-fluid mt-3 app_body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold text-primary mb-0">
                            ZFERs Liberados sem QR Code
                        </h4>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={fetchZfers}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="spinner-border spinner-border-sm me-1" />
                                ) : (
                                    <i className="bi bi-arrow-clockwise me-1" />
                                )}
                                Atualizar
                            </button>
                            <button className="btn btn-sm btn-primary" onClick={abrirModal}>
                                <i className="bi bi-plus-lg me-1" />
                                Adicionar ZFER
                            </button>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="text-muted mt-2">Carregando ZFERs...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover align-middle fw-bold" style={{ fontSize: "1.1rem" }}>
                                <thead className="table-primary">
                                    <tr>
                                        <th>ZFER</th>
                                        <th>Criado por</th>
                                        <th>Data / Hora</th>
                                        <th style={{ width: 110 }}>Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {zfers.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted">
                                                Nenhum ZFER liberado
                                            </td>
                                        </tr>
                                    ) : (
                                        zfers.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.codMaterial}</td>
                                                <td>{item.criado_Por || "-"}</td>
                                                <td>
                                                    {item._ts_Creat
                                                        ? new Date(item._ts_Creat).toLocaleString("pt-BR")
                                                        : "-"}
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => confirmarExclusao(item)}
                                                    >
                                                        <i className="bi bi-trash me-1" />
                                                        Excluir
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="text-muted small mt-2">
                        {zfers.length} ZFER(s) liberado(s)
                    </div>
                </div>
            </div>

            {/* Modal Confirmar Exclusão */}
            <Modal show={showModalExcluir} onHide={() => setShowModalExcluir(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Exclusão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza que deseja excluir o ZFER{" "}
                    <strong>{itemExcluir?.codMaterial}</strong> da lista?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalExcluir(false)} disabled={excluindo}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleExcluir} disabled={excluindo}>
                        {excluindo ? (
                            <Spinner animation="border" size="sm" className="me-1" />
                        ) : (
                            <i className="bi bi-trash me-1" />
                        )}
                        Excluir
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Adicionar ZFER */}
            <Modal show={showModal} onHide={fecharModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Adicionar ZFER sem QR Code</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label className="form-label fw-semibold">Código do Material</label>
                    <div className="input-group mb-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Digite o código do material..."
                            value={inputZfer}
                            onChange={(e) => {
                                setInputZfer(e.target.value);
                                setMaterialEncontrado(null);
                            }}
                            onKeyDown={handleKeyDown}
                            disabled={pesquisando || adicionando}
                            autoFocus
                        />
                        <button
                            className="btn btn-outline-primary"
                            onClick={handlePesquisar}
                            disabled={pesquisando || !inputZfer.trim()}
                        >
                            {pesquisando ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <>
                                    <i className="bi bi-search me-1" />
                                    Pesquisar
                                </>
                            )}
                        </button>
                    </div>

                    {materialEncontrado && (
                        <div className={`card border-${materialEncontrado.jaCadastradoSemQrCode ? "warning" : "success"}`}>
                            {materialEncontrado.jaCadastradoSemQrCode && (
                                <div className="alert alert-warning mb-0 py-2 rounded-bottom-0 text-center">
                                    <i className="bi bi-exclamation-triangle me-1" />
                                    Este material já está cadastrado na lista.
                                </div>
                            )}
                            <div className="card-body py-2">
                                <div className="row g-2">
                                    <div className="col-6">
                                        <small className="text-muted">Código Material</small>
                                        <div className="fw-bold">{materialEncontrado.codMaterial}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Partnumber</small>
                                        <div className="fw-bold">{materialEncontrado.partnumber || "-"}</div>
                                    </div>
                                    <div className="col-12">
                                        <small className="text-muted">Descrição</small>
                                        <div className="fw-bold">{materialEncontrado.desProduto || "-"}</div>
                                    </div>
                                    <div className="col-4">
                                        <small className="text-muted">Cliente / Marca</small>
                                        <div className="fw-bold">{materialEncontrado.vehBrand || "-"}</div>
                                    </div>
                                    <div className="col-4">
                                        <small className="text-muted">Modelo</small>
                                        <div className="fw-bold">{materialEncontrado.vehModel || "-"}</div>
                                    </div>
                                    <div className="col-4">
                                        <small className="text-muted">Tipo Veículo</small>
                                        <div className="fw-bold">{materialEncontrado.vehCode || "-"}</div>
                                    </div>
                                    <div className="col-4">
                                        <small className="text-muted">Fórmula</small>
                                        <div className="fw-bold">{materialEncontrado.formulaCod || "-"}</div>
                                    </div>
                                    <div className="col-4">
                                        <small className="text-muted">Sub Produto</small>
                                        <div className="fw-bold">{materialEncontrado.subProduto || "-"}</div>
                                    </div>
                                    <div className="col-4">
                                        <small className="text-muted">Tipo Produto</small>
                                        <div className="fw-bold">{materialEncontrado.productType || "-"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={fecharModal} disabled={adicionando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAdicionar}
                        disabled={!materialEncontrado || adicionando || materialEncontrado?.jaCadastradoSemQrCode}
                    >
                        {adicionando ? (
                            <Spinner animation="border" size="sm" className="me-1" />
                        ) : (
                            <i className="bi bi-plus-lg me-1" />
                        )}
                        Adicionar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ZferSemQrcode;
